/**
 * A QUIÉN LLAMAR SI ALGUIEN SE CAE EN LA CANCHA.
 *
 * Javier: «es un deporte de riesgo y las personas pueden actuar
 * inmeditamente». Todo lo de esta pantalla existe para que alguien que no
 * conoce a la persona pueda ayudarla en los primeros minutos.
 *
 * TRES DECISIONES QUE VALE LA PENA DEJAR ESCRITAS:
 *
 * 1. EL TIPO DE SANGRE SON BOTONES **Y** UNA CAJA DE TEXTO, no un
 *    desplegable. Quien sabe el suyo lo elige de un toque, que es la mayoría;
 *    quien no está seguro escribe «creo que O+» o «no sé», y eso es MÁS útil
 *    que un campo vacío — le dice a quien atiende que pregunte en vez de
 *    asumir. Una lista cerrada obliga a elegir mal o a no poner nada.
 *
 * 2. NADA ES OBLIGATORIO. El botón de guardar no se apaga nunca: guardar solo
 *    el tipo de sangre, o solo un teléfono, es una respuesta completa. Pedir
 *    los tres campos para aceptar uno es cómo se consigue que la gente no
 *    llene ninguno.
 *
 * 3. LAS FILAS EN BLANCO NO SON UN ERROR. Se tiran al guardar —lo hace
 *    `emergencyToSave`, en el servidor— y no se le dice nada a nadie: quien
 *    agregó una fila de más y no la usó no hizo nada mal.
 *
 * SE GUARDA POR SU PROPIO CAMINO —`PUT /profile/emergency`— y no con el
 * formulario del perfil. Ver el contrato: si viviera ahí, guardar la cédula
 * borraría el tipo de sangre en silencio.
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BLOOD_TYPES,
  EMERGENCY_BLOOD_TYPE_MAX,
  EMERGENCY_CONTACTS_MAX,
  EMERGENCY_CONTACT_NAME_MAX,
  EMERGENCY_CONTACT_PHONE_MAX,
  EMERGENCY_CONTACT_RELATIONSHIP_MAX,
  EMERGENCY_MEDICAL_NOTES_MAX,
  type EmergencyInfo,
  type PlayerProfile,
} from '@tennisup/contracts';

import { Alert } from './Alert';
import { Plegable } from './Plegable';
import { Field } from './Field';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';

/**
 * UNA FILA DEL FORMULARIO, que NO es un contacto guardado.
 *
 * LLEVA `key` PROPIA Y NO EL ÍNDICE DEL ARREGLO. Con el índice, borrar la
 * primera de tres le mueve el texto a las otras dos: React reutiliza el input
 * y se queda con lo que había escrito. Es el error clásico de las listas
 * editables, y acá se paga caro — borrar a la mamá y que el teléfono del
 * vecino aparezca en su fila.
 */
interface Fila {
  key: string;
  name: string;
  relationship: string;
  phone: string;
}

let siguienteKey = 0;
function filaVacía(): Fila {
  siguienteKey += 1;
  return { key: `nueva-${siguienteKey}`, name: '', relationship: '', phone: '' };
}

/**
 * LO QUE HAY, AUNQUE NO VENGA NADA.
 *
 * `profile.emergency` PUEDE LLEGAR `undefined` aunque el tipo diga que no, y
 * eso no es paranoia de tipos: el perfil viene por la red, y un navegador con
 * la aplicación nueva contra una API todavía sin desplegar recibe el perfil
 * VIEJO, sin este campo. Sin esta función, leerlo directo tira
 * `Cannot read properties of undefined` MIENTRAS SE PINTA, y React no deja
 * media pantalla rota: desmonta el árbol entero. O sea que un campo que
 * falta no esconde una sección — deja el perfil COMPLETO en blanco, sin
 * foto, sin datos y sin contraseña.
 *
 * Lo encontró una prueba de `ProfilePage` cuyo perfil de mentira no tenía el
 * campo, que es exactamente la forma del problema de verdad.
 */
function loQueHaya(info: EmergencyInfo | undefined): EmergencyInfo {
  return info ?? { bloodType: null, medicalNotes: null, contacts: [] };
}

function filasDe(info: EmergencyInfo): Fila[] {
  return info.contacts.map((contacto) => ({
    key: contacto.id,
    name: contacto.name,
    relationship: contacto.relationship ?? '',
    phone: contacto.phone,
  }));
}

/** ¿Este texto es uno de los que se ofrecen de un toque? */
function esDeLaLista(valor: string): boolean {
  return (BLOOD_TYPES as readonly string[]).includes(valor);
}

interface Props {
  profile: PlayerProfile;
  /** Para que la pantalla de arriba se quede con lo que devolvió el servidor. */
  onSaved: (profile: PlayerProfile) => void;
}

export function EmergencySection({ profile, onSaved }: Props): JSX.Element {
  const { t } = useTranslation();

  const inicial = loQueHaya(profile.emergency);
  const [bloodType, setBloodType] = useState(inicial.bloodType ?? '');
  const [medicalNotes, setMedicalNotes] = useState(inicial.medicalNotes ?? '');
  const [filas, setFilas] = useState<Fila[]>(() => filasDe(inicial));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  function parchar(key: string, cambio: Partial<Fila>): void {
    setSaved(false);
    setFilas((actuales) =>
      actuales.map((fila) => (fila.key === key ? { ...fila, ...cambio } : fila)),
    );
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (sending) return;

    setSending(true);
    setError(null);
    try {
      const guardado = await api.setEmergency({
        bloodType: bloodType.trim() === '' ? null : bloodType.trim(),
        medicalNotes: medicalNotes.trim() === '' ? null : medicalNotes.trim(),
        /*
         * LAS FILAS EN BLANCO VIAJAN Y EL SERVIDOR LAS TIRA. Filtrarlas
         * también acá sería tener la misma regla en dos lados, y el día que
         * cambie una nadie se acuerda de la otra.
         */
        contacts: filas.map((fila) => ({
          name: fila.name,
          relationship: fila.relationship.trim() === '' ? null : fila.relationship,
          phone: fila.phone,
        })),
      });
      /*
       * SE REDIBUJA CON LO QUE CONTESTÓ EL SERVIDOR, no con lo que había en
       * la pantalla: así las filas que se tiraron desaparecen a la vista y
       * cada una queda con su id de verdad.
       */
      const quedó = loQueHaya(guardado.emergency);
      setFilas(filasDe(quedó));
      setBloodType(quedó.bloodType ?? '');
      setMedicalNotes(quedó.medicalNotes ?? '');
      setSaved(true);
      onSaved(guardado);
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setSending(false);
    }
  }

  const lleno = filas.length >= EMERGENCY_CONTACTS_MAX;

  return (
    <Plegable titulo={t('profile.emergencyTitle')} resumen={t('profile.emergencyHint')}>
      <p className="help">{t('profile.emergencyHelp')}</p>

      {error !== null && <Alert kind="error">{error}</Alert>}
      {saved && <Alert kind="success">{t('profile.emergencySaved')}</Alert>}

      <form onSubmit={(event) => void onSubmit(event)} noValidate>
        <Field
          id="em-blood"
          label={t('profile.emergencyBloodType')}
          help={t('profile.emergencyBloodTypeHelp')}
        >
          {(control) => (
            <>
              {/*
                LOS OCHO DE UN TOQUE. Son `button` y no radios a propósito: un
                radio marcado no se puede desmarcar, y acá tocar el que ya está
                puesto tiene que poder borrarlo — si alguien se equivocó de
                grupo, la única salida sería escribir encima a mano.
              */}
              <div className="day-picker">
                {BLOOD_TYPES.map((tipo) => (
                  <button
                    key={tipo}
                    type="button"
                    className={bloodType === tipo ? 'day-chip on' : 'day-chip'}
                    aria-pressed={bloodType === tipo}
                    onClick={() => {
                      setSaved(false);
                      setBloodType((actual) => (actual === tipo ? '' : tipo));
                    }}
                  >
                    {tipo}
                  </button>
                ))}
              </div>
              {/*
                Y LA CAJA, SIEMPRE VISIBLE. No aparece al tocar «Otro»: quien
                no sabe su tipo tendría que adivinar que hay un botón para
                decirlo. Escribir acá apaga el chip y elegir un chip vacía la
                caja — son la misma cosa vista de dos formas.
              */}
              <input
                {...control}
                value={esDeLaLista(bloodType) ? '' : bloodType}
                maxLength={EMERGENCY_BLOOD_TYPE_MAX}
                placeholder={t('profile.emergencyBloodTypeOther')}
                onChange={(e) => {
                  setSaved(false);
                  setBloodType(e.target.value);
                }}
              />
            </>
          )}
        </Field>

        <div className="field">
          <label htmlFor="em-medical">{t('profile.emergencyMedicalNotes')}</label>
          <textarea
            id="em-medical"
            rows={4}
            maxLength={EMERGENCY_MEDICAL_NOTES_MAX}
            value={medicalNotes}
            onChange={(e) => {
              setSaved(false);
              setMedicalNotes(e.target.value);
            }}
          />
          <p className="help">{t('profile.emergencyMedicalNotesHelp')}</p>
        </div>

        <h3>{t('profile.emergencyContacts')}</h3>
        <p className="help">{t('profile.emergencyContactsHelp')}</p>

        {filas.length === 0 && <p className="help">{t('profile.emergencyNoContacts')}</p>}

        <ul className="emergency-list">
          {filas.map((fila, posición) => (
            <li key={fila.key} className="emergency-row">
              <div className="row">
                <Field id={`em-name-${fila.key}`} label={t('profile.emergencyContactName')}>
                  {(control) => (
                    <input
                      {...control}
                      value={fila.name}
                      maxLength={EMERGENCY_CONTACT_NAME_MAX}
                      onChange={(e) => parchar(fila.key, { name: e.target.value })}
                    />
                  )}
                </Field>

                <Field id={`em-phone-${fila.key}`} label={t('profile.emergencyContactPhone')}>
                  {(control) => (
                    <input
                      {...control}
                      // `tel` Y NO TEXTO: en el teléfono abre el teclado
                      // numérico, que es donde se llena esto casi siempre.
                      type="tel"
                      value={fila.phone}
                      maxLength={EMERGENCY_CONTACT_PHONE_MAX}
                      onChange={(e) => parchar(fila.key, { phone: e.target.value })}
                    />
                  )}
                </Field>
              </div>

              <Field
                id={`em-rel-${fila.key}`}
                label={t('profile.emergencyContactRelationship')}
                help={t('profile.emergencyContactRelationshipHelp')}
              >
                {(control) => (
                  <input
                    {...control}
                    value={fila.relationship}
                    maxLength={EMERGENCY_CONTACT_RELATIONSHIP_MAX}
                    onChange={(e) => parchar(fila.key, { relationship: e.target.value })}
                  />
                )}
              </Field>

              <div className="row-actions">
                <button
                  type="button"
                  className="link danger"
                  aria-label={t('profile.emergencyRemoveContact', {
                    name: fila.name.trim() === '' ? String(posición + 1) : fila.name,
                  })}
                  onClick={() => {
                    setSaved(false);
                    setFilas((actuales) => actuales.filter((otra) => otra.key !== fila.key));
                  }}
                >
                  {t('common.remove')}
                </button>
              </div>
            </li>
          ))}
        </ul>

        {lleno && (
          <p className="help">{t('profile.emergencyFull', { count: EMERGENCY_CONTACTS_MAX })}</p>
        )}

        {/*
          LOS DOS BOTONES EN UNA FILA, SEPARADOS. Javier: «hay que separar los
          botones». Estaban sueltos uno detrás del otro y quedaban pegados:
          «Agregar un contacto» y «Guardar» se leían como un solo bloque, y el
          de agregar —que es secundario— competía con el de guardar.

          `button-row` es la misma fila que usa el resto de la aplicación, con
          su separación y su permiso para bajar a la siguiente línea cuando no
          caben: a 320 px estos dos textos no entran juntos.
        */}
        <div className="button-row">
          {!lleno && (
            <button
              type="button"
              className="link"
              onClick={() => {
                setSaved(false);
                setFilas((actuales) => [...actuales, filaVacía()]);
              }}
            >
              {t('profile.emergencyAddContact')}
            </button>
          )}

          {/*
            NUNCA DESHABILITADO salvo mientras se manda. Guardar con todo vacío
            es cómo se borra lo que se puso antes, y eso tiene que poder hacerse.
          */}
          <button type="submit" disabled={sending}>
            {sending ? t('common.sending') : t('profile.emergencySave')}
          </button>
        </div>
      </form>
    </Plegable>
  );
}
